import React, { useEffect, useMemo, useState } from 'react';
import parse from 'autosuggest-highlight/parse';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Autocomplete, TextField, Grid, Typography, Box } from '@mui/material';
import { debounce } from 'lodash';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function placesLibraryReady(): boolean {
  const w = window as unknown as {
    google?: { maps?: { places?: { AutocompleteService?: unknown } } };
  };
  return Boolean(w.google?.maps?.places?.AutocompleteService);
}

/** Resolve when Maps JS has finished loading and `maps.places` is usable. */
function loadGoogleMapsPlaces(apiKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('no window'));
      return;
    }

    if (placesLibraryReady()) {
      resolve();
      return;
    }

    const finish = () => {
      if (placesLibraryReady()) resolve();
      else reject(new Error('Google Maps Places library unavailable'));
    };

    let script = document.querySelector('#google-maps') as HTMLScriptElement | null;
    if (script) {
      const onLoad = () => {
        script?.removeEventListener('load', onLoad);
        finish();
      };
      script.addEventListener('load', onLoad);
      script.addEventListener('error', () =>
        reject(new Error('Google Maps script failed'))
      );
      if (placesLibraryReady()) {
        script.removeEventListener('load', onLoad);
        finish();
      }
      return;
    }

    script = document.createElement('script');
    script.id = 'google-maps';
    script.async = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.onload = () => finish();
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
}

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}
interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}
interface PlaceType {
  description: string;
  place_id: string;
  structured_formatting: StructuredFormatting;
}

/** Set after Maps JS `places` library loads (see `loadGoogleMapsPlaces`). */
const autocompleteService: {
  current: {
    getQueryPredictions: (
      request: { input: string },
      callback: (results?: readonly PlaceType[]) => void
    ) => void;
  } | null;
} = { current: null };

type PropTypes = {
  location?: string;
  onSelectLocation: (
    location: string | null,
    locationPlaceId: string | null,
    locationLatitude: number | null,
    locationLongitude: number | null
  ) => void;
};

export const LocationsDropdown = ({
  location,
  onSelectLocation,
}: PropTypes) => {
  const [value, setValue] = useState<PlaceType | null>();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<readonly PlaceType[]>([]);
  const [mapsReady, setMapsReady] = useState(false);
  const [mapsLoadError, setMapsLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!location) return;
    const obj = {
      description: location,
      matched_substrings: [],
      place_id: null,
      reference: null,
      structured_formatting: {
        main_text: '',
        main_text_matched_substrings: [],
        secondary_text: '',
      },
      terms: [],
      types: [],
    };
    setValue(obj as unknown as PlaceType);
  }, [location]);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) return;

    let cancelled = false;
    loadGoogleMapsPlaces(GOOGLE_MAPS_API_KEY)
      .then(() => {
        if (cancelled) return;
        const w = window as unknown as {
          google?: {
            maps?: {
              places?: {
                AutocompleteService?: new () => NonNullable<
                  typeof autocompleteService.current
                >;
              };
            };
          };
        };
        const AC = w.google?.maps?.places?.AutocompleteService;
        if (AC && !autocompleteService.current) {
          autocompleteService.current = new AC();
        }
        if (autocompleteService.current) {
          setMapsReady(true);
        } else if (!cancelled) {
          setMapsLoadError(
            'Google Maps loaded but Places Autocomplete is unavailable. Enable Places API for your key.'
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMapsLoadError('Could not load Google Maps. Check API key and enabled APIs.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const fetchAuto = useMemo(
    () =>
      debounce(
        (
          request: { input: string },
          callback: (results?: readonly PlaceType[]) => void
        ) => {
          autocompleteService.current?.getQueryPredictions(request, callback);
        },
        400
      ),
    []
  );

  useEffect(() => {
    return () => fetchAuto.cancel();
  }, [fetchAuto]);

  useEffect(() => {
    let active = true;

    if (!mapsReady || !autocompleteService.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetchAuto({ input: inputValue }, (results?: readonly PlaceType[]) => {
      if (active) {
        let newOptions: readonly PlaceType[] = [];
        if (value) {
          newOptions = [value];
        }
        if (results) {
          newOptions = [...newOptions, ...results];
        }
        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [mapsReady, value, inputValue, fetchAuto]);

  const storeCoordinates = (loc: string, placeId: string) => {
    const w = window as unknown as {
      google?: { maps?: { Geocoder?: new () => { geocode: (...args: unknown[]) => void } } };
    };
    const maps = w.google?.maps;
    if (!maps?.Geocoder) return;
    const geocoder = new maps.Geocoder();
    geocoder.geocode({ placeId: placeId }, function (results: any, status: string) {
      if (status !== 'OK' || !results?.[0]?.geometry?.location) {
        return;
      }
      const lat = results[0].geometry.location.lat();
      const lng = results[0].geometry.location.lng();
      onSelectLocation(loc, placeId, lat, lng);
    });
  };

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <Typography variant="body2" color="text.secondary">
        Add <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> to your env file to enable
        location search.
      </Typography>
    );
  }

  if (mapsLoadError) {
    return (
      <Typography variant="body2" color="error">
        {mapsLoadError}
      </Typography>
    );
  }

  if (!mapsReady) {
    return (
      <TextField
        variant="standard"
        label="Add a location"
        fullWidth
        disabled
        placeholder="Loading maps…"
      />
    );
  }

  return (
    <Autocomplete
      sx={{ width: '100%' }}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value || null}
      noOptionsText="No locations"
      onChange={(event: unknown, newValue: PlaceType | null) => {
        if (newValue?.place_id) {
          storeCoordinates(newValue.description, newValue.place_id);
          setOptions(newValue ? [newValue, ...options] : options);
        } else if (!newValue) {
          onSelectLocation(null, null, null, null);
        }
        setValue(newValue ?? undefined);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          label="Add a location"
          fullWidth
        />
      )}
      renderOption={(props, option) => {
        const matches =
          option.structured_formatting.main_text_matched_substrings || [];

        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match: MainTextMatchedSubstrings) => [
            match.offset,
            match.offset + match.length,
          ])
        );

        return (
          <li {...props}>
            <Grid container alignItems="center">
              <Grid item sx={{ display: 'flex', width: 44 }}>
                <LocationOnIcon sx={{ color: 'text.secondary' }} />
              </Grid>
              <Grid
                item
                sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}
              >
                {parts.map((part, index) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                  >
                    {part.text}
                  </Box>
                ))}
                <Typography variant="body2" color="text.secondary">
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
};
